import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import { useImpersonation } from '@/hooks/useImpersonation';
import type { Me, OutreachInviter } from '@/api/types';
import type { ContactPatch } from '@/lib/contactDetails';

/**
 * Resolves the caller's role + identity from the backend. Used to:
 *   - branch the layout (admin vs outreacher)
 *   - decide whether to show the ClaimPending screen on 403
 *
 * When an admin is impersonating an outreacher, the `?as=<inviterId>` param
 * is sent so the backend returns outreacher-shaped data.
 */
export function useMe() {
  const api = useApi();
  const { inviterId } = useImpersonation();
  return useQuery({
    queryKey: ['me', inviterId],
    queryFn: () =>
      api.get<Me>('/portal/me', inviterId ? { as: inviterId } : undefined),
    retry: (failureCount, error) => {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('OUTREACH_INVITER_NOT_FOUND')) return false;
      return failureCount < 1;
    },
  });
}

/** Save the outreacher's own contact details (the only part of their record they can write). */
export function useUpdateMyContact() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ContactPatch) => api.patch<OutreachInviter>('/portal/profile/contact', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}
